import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from '../hooks/use-toast';
import { messagesAPI } from '../utils/api';
import api from '../utils/api';
import {
  Mail, Send, Inbox, ArrowLeft, Reply, Clock,
  Loader2, PenSquare, Building2, User, FileText, ChevronRight
} from 'lucide-react';

const StudentMessages = () => {
  const [view, setView] = useState('applications'); // 'applications', 'inbox', 'sent', 'compose', 'detail', 'app-detail'
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [composeData, setComposeData] = useState({ recipientId: '', subject: '', content: '' });
  const [replyContent, setReplyContent] = useState('');
  const [sending, setSending] = useState(false);
  
  // Application messages state
  const [applications, setApplications] = useState([]);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [appMessages, setAppMessages] = useState([]);
  const [appReplyContent, setAppReplyContent] = useState('');

  useEffect(() => {
    if (view === 'inbox') {
      fetchInbox();
    } else if (view === 'sent') {
      fetchSent();
    } else if (view === 'applications') {
      fetchApplications();
    }
  }, [view]);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const response = await api.get('/applications');
      // Filter to only show submitted applications
      const submittedApps = response.data.filter(app => app.status === 'submitted');
      setApplications(submittedApps);
    } catch (error) {
      console.error('Failed to fetch applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewApplicationMessages = async (app) => {
    try {
      setLoading(true);
      const response = await messagesAPI.getApplicationMessages(app._id);
      setSelectedApplication({ ...app, college_name: response.college_name || app.college_name });
      setAppMessages(response.messages || []);
      setView('app-detail');
    } catch (error) {
      console.error('Failed to fetch application messages:', error);
      setSelectedApplication(app);
      setAppMessages(app.messages || []);
      setView('app-detail');
    } finally {
      setLoading(false);
    }
  };

  const handleSendAppReply = async () => {
    if (!appReplyContent.trim()) return;
    
    try {
      setSending(true);
      await messagesAPI.studentReplyToApplication(selectedApplication._id, appReplyContent);
      toast({ title: "Success", description: "Reply sent to university" });
      setAppReplyContent('');
      // Refresh messages
      const response = await messagesAPI.getApplicationMessages(selectedApplication._id);
      setAppMessages(response.messages || []);
    } catch (error) {
      toast({ title: "Error", description: "Failed to send reply", variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  const getUnreadAppMessagesCount = (app) => {
    return (app.messages || []).filter(m => m.from === 'university' && !m.read).length;
  };

  const fetchInbox = async () => {
    try {
      setLoading(true);
      const data = await messagesAPI.getStudentInbox();
      setMessages(data.messages || []);
      setUnreadCount(data.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch inbox:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSent = async () => {
    try {
      setLoading(true);
      const data = await messagesAPI.getStudentSent();
      setMessages(data.messages || []);
    } catch (error) {
      console.error('Failed to fetch sent:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewMessage = async (message) => {
    try {
      const fullMessage = await messagesAPI.getStudentMessage(message._id);
      setSelectedMessage(fullMessage);
      setView('detail');
      // Refresh inbox to update unread count
      if (!message.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to load message", variant: "destructive" });
    }
  };

  const handleSendMessage = async () => {
    if (!composeData.recipientId || !composeData.subject || !composeData.content) {
      toast({ title: "Error", description: "Please fill all fields", variant: "destructive" });
      return;
    }

    try {
      setSending(true);
      await messagesAPI.studentSendMessage(composeData.recipientId, composeData.subject, composeData.content);
      toast({ title: "Success", description: "Message sent successfully" });
      setComposeData({ recipientId: '', subject: '', content: '' });
      setView('sent');
    } catch (error) {
      toast({ title: "Error", description: "Failed to send message", variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  const handleReply = async () => {
    if (!replyContent.trim()) {
      toast({ title: "Error", description: "Please enter a reply", variant: "destructive" });
      return;
    }

    try {
      setSending(true);
      await messagesAPI.studentReplyMessage(selectedMessage._id, replyContent);
      toast({ title: "Success", description: "Reply sent successfully" });
      setReplyContent('');
      // Refresh the message to show the reply
      const updated = await messagesAPI.getStudentMessage(selectedMessage._id);
      setSelectedMessage(updated);
    } catch (error) {
      toast({ title: "Error", description: "Failed to send reply", variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto px-4">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold">Messages</h1>
            <p className="text-gray-600">Communicate with universities</p>
          </div>
          {view !== 'compose' && (
            <Button onClick={() => setView('compose')} className="bg-emerald-600 hover:bg-emerald-700">
              <PenSquare className="h-4 w-4 mr-2" />
              Compose
            </Button>
          )}
        </div>

        <div className="grid md:grid-cols-4 gap-6">
          {/* Sidebar */}
          <div className="md:col-span-1">
            <Card>
              <CardContent className="p-2">
                <button
                  onClick={() => setView('applications')}
                  className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
                    view === 'applications' || view === 'app-detail' ? 'bg-emerald-50 text-emerald-700' : 'hover:bg-gray-100'
                  }`}
                  data-testid="nav-applications"
                >
                  <span className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Applications
                  </span>
                  {applications.reduce((count, app) => count + getUnreadAppMessagesCount(app), 0) > 0 && (
                    <Badge className="bg-blue-600">
                      {applications.reduce((count, app) => count + getUnreadAppMessagesCount(app), 0)}
                    </Badge>
                  )}
                </button>
                <button
                  onClick={() => setView('inbox')}
                  className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
                    view === 'inbox' || view === 'detail' ? 'bg-emerald-50 text-emerald-700' : 'hover:bg-gray-100'
                  }`}
                  data-testid="nav-inbox"
                >
                  <span className="flex items-center gap-2">
                    <Inbox className="h-4 w-4" />
                    Inbox
                  </span>
                  {unreadCount > 0 && (
                    <Badge className="bg-emerald-600">{unreadCount}</Badge>
                  )}
                </button>
                <button
                  onClick={() => setView('sent')}
                  className={`w-full flex items-center gap-2 p-3 rounded-lg transition-colors ${
                    view === 'sent' ? 'bg-emerald-50 text-emerald-700' : 'hover:bg-gray-100'
                  }`}
                  data-testid="nav-sent"
                >
                  <Send className="h-4 w-4" />
                  Sent
                </button>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="md:col-span-3">
            {/* Applications Messages View */}
            {view === 'applications' && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Application Messages
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
                    </div>
                  ) : applications.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No submitted applications yet</p>
                      <p className="text-sm">Messages from universities about your applications will appear here</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {applications.map((app) => (
                        <div
                          key={app._id}
                          onClick={() => handleViewApplicationMessages(app)}
                          className="flex items-center justify-between p-4 border rounded-lg hover:border-emerald-300 hover:bg-emerald-50/50 cursor-pointer transition-colors"
                          data-testid={`app-message-item-${app._id}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="bg-emerald-100 p-2 rounded-full">
                              <Building2 className="h-5 w-5 text-emerald-600" />
                            </div>
                            <div>
                              <h3 className="font-medium">{app.college_name}</h3>
                              <p className="text-sm text-gray-500">
                                {(app.messages || []).length > 0 
                                  ? `${(app.messages || []).length} message(s)` 
                                  : 'No messages yet'}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            {getUnreadAppMessagesCount(app) > 0 && (
                              <Badge className="bg-blue-600">{getUnreadAppMessagesCount(app)} new</Badge>
                            )}
                            <ChevronRight className="h-5 w-5 text-gray-400" />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Application Messages Detail View */}
            {view === 'app-detail' && selectedApplication && (
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => setView('applications')} data-testid="back-btn">
                      <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Building2 className="h-5 w-5 text-emerald-600" />
                        {selectedApplication.college_name}
                      </CardTitle>
                      <p className="text-sm text-gray-500">Application Messages</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Messages Thread */}
                  {appMessages.length > 0 ? (
                    <div className="max-h-96 overflow-y-auto space-y-3 p-4 bg-gray-50 rounded-lg border">
                      {appMessages.map((msg, idx) => (
                        <div
                          key={idx}
                          className={`p-3 rounded-lg ${
                            msg.from === 'university'
                              ? 'bg-blue-50 mr-8 border-l-4 border-blue-400'
                              : 'bg-emerald-50 ml-8 border-l-4 border-emerald-400'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium flex items-center gap-1">
                              {msg.from === 'university' ? (
                                <>
                                  <Building2 className="h-3 w-3" />
                                  {msg.from_name || selectedApplication.college_name}
                                </>
                              ) : (
                                <>
                                  <User className="h-3 w-3" />
                                  You
                                </>
                              )}
                            </span>
                            <span className="text-xs text-gray-500">{formatDate(msg.timestamp)}</span>
                          </div>
                          {msg.subject && (
                            <p className="text-xs text-gray-600 mb-1">Subject: {msg.subject}</p>
                          )}
                          <p className="text-sm text-gray-700">{msg.content}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-gray-50 rounded-lg text-gray-500">
                      <Mail className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No messages yet from this university</p>
                      <p className="text-sm">Start a conversation by sending a message below</p>
                    </div>
                  )}

                  {/* Reply Form */}
                  <div className="space-y-3 pt-4 border-t">
                    <Textarea
                      placeholder="Write a message to the university..."
                      value={appReplyContent}
                      onChange={(e) => setAppReplyContent(e.target.value)}
                      rows={3}
                      data-testid="app-reply-textarea"
                    />
                    <Button
                      onClick={handleSendAppReply}
                      disabled={!appReplyContent.trim() || sending}
                      className="w-full bg-emerald-600 hover:bg-emerald-700"
                      data-testid="send-app-reply-btn"
                    >
                      {sending ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4 mr-2" />
                      )}
                      Send Message
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Compose View */}
            {view === 'compose' && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PenSquare className="h-5 w-5" />
                    New Message
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">To (University ID)</label>
                    <Input
                      placeholder="Enter university ID..."
                      value={composeData.recipientId}
                      onChange={(e) => setComposeData({ ...composeData, recipientId: e.target.value })}
                    />
                    <p className="text-xs text-gray-500 mt-1">You can find the university ID from your application details</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Subject</label>
                    <Input
                      placeholder="Message subject..."
                      value={composeData.subject}
                      onChange={(e) => setComposeData({ ...composeData, subject: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Message</label>
                    <Textarea
                      placeholder="Write your message..."
                      rows={8}
                      value={composeData.content}
                      onChange={(e) => setComposeData({ ...composeData, content: e.target.value })}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => setView('inbox')} variant="outline">Cancel</Button>
                    <Button onClick={handleSendMessage} disabled={sending} className="bg-emerald-600 hover:bg-emerald-700">
                      {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                      Send Message
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Message Detail View */}
            {view === 'detail' && selectedMessage && (
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-2 mb-4">
                    <Button variant="ghost" size="sm" onClick={() => setView('inbox')}>
                      <ArrowLeft className="h-4 w-4 mr-1" />
                      Back
                    </Button>
                  </div>
                  <CardTitle>{selectedMessage.subject}</CardTitle>
                  <div className="flex items-center gap-4 text-sm text-gray-600 mt-2">
                    <span className="flex items-center gap-1">
                      <Building2 className="h-4 w-4" />
                      {selectedMessage.sender?.name || 'University'}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      {formatDate(selectedMessage.created_at)}
                    </span>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Thread messages */}
                  {selectedMessage.thread && selectedMessage.thread.length > 0 ? (
                    <div className="space-y-4 mb-6">
                      {selectedMessage.thread.map((msg, index) => (
                        <div
                          key={index}
                          className={`p-4 rounded-lg ${
                            msg.sender_type === 'student' ? 'bg-emerald-50 ml-8' : 'bg-gray-50 mr-8'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-sm">
                              {msg.sender_type === 'student' ? 'You' : selectedMessage.sender?.name}
                            </span>
                            <span className="text-xs text-gray-500">{formatDate(msg.created_at)}</span>
                          </div>
                          <p className="text-gray-700 whitespace-pre-wrap">{msg.content}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-4 bg-gray-50 rounded-lg mb-6">
                      <p className="text-gray-700 whitespace-pre-wrap">{selectedMessage.content}</p>
                    </div>
                  )}

                  {/* Reply section */}
                  <div className="border-t pt-4">
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <Reply className="h-4 w-4" />
                      Reply
                    </h4>
                    <Textarea
                      placeholder="Write your reply..."
                      rows={4}
                      value={replyContent}
                      onChange={(e) => setReplyContent(e.target.value)}
                    />
                    <div className="flex justify-end mt-2">
                      <Button onClick={handleReply} disabled={sending} className="bg-emerald-600 hover:bg-emerald-700">
                        {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                        Send Reply
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* List View (Inbox/Sent) */}
            {(view === 'inbox' || view === 'sent') && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    {view === 'inbox' ? <Inbox className="h-5 w-5" /> : <Send className="h-5 w-5" />}
                    {view === 'inbox' ? 'Inbox' : 'Sent Messages'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <Mail className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No messages yet</p>
                      {view === 'inbox' && (
                        <p className="text-sm mt-1">Messages from universities will appear here</p>
                      )}
                    </div>
                  ) : (
                    <div className="divide-y">
                      {messages.map((message) => (
                        <button
                          key={message._id}
                          onClick={() => handleViewMessage(message)}
                          className={`w-full text-left p-4 hover:bg-gray-50 transition-colors ${
                            !message.is_read && view === 'inbox' ? 'bg-emerald-50/50' : ''
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                {!message.is_read && view === 'inbox' && (
                                  <div className="w-2 h-2 bg-emerald-500 rounded-full" />
                                )}
                                <span className={`font-medium truncate ${!message.is_read && view === 'inbox' ? 'text-gray-900' : 'text-gray-700'}`}>
                                  {view === 'inbox' 
                                    ? message.sender?.name || 'University' 
                                    : message.recipient?.name || 'University'}
                                </span>
                              </div>
                              <p className={`truncate ${!message.is_read && view === 'inbox' ? 'font-medium' : ''}`}>
                                {message.subject}
                              </p>
                              <p className="text-sm text-gray-500 truncate">{message.content}</p>
                            </div>
                            <span className="text-xs text-gray-400 ml-4 whitespace-nowrap">
                              {formatDate(message.created_at)}
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentMessages;
